import { gql } from '@apollo/client';

/* ── universal time series ──────────────────────────────────── */

export const PORTFOLIO_VALUE_HISTORY = gql`
  query PortfolioValueHistory($securitySymbol: String, $goalId: Int, $fromDate: Date, $toDate: Date) {
    getPortfolioValueHistory(securitySymbol: $securitySymbol, goalId: $goalId, fromDate: $fromDate, toDate: $toDate) {
      latestDate
      marketValue
      dailyReturn
    }
  }
`;

/**
 * Risk metrics over time, for the subject and for each of its holdings.
 *
 * A row carries both measurements — `realized*` from the historical pass,
 * `forecast*` from the simulation run — and most subjects have only one of
 * them, so asking for both is what makes a series appear at all: a scheme has
 * forecast runs and no realised pass. Tracking error is among them, which the
 * decomposition panel's figures are.
 *
 * One row per DATE PER SCENARIO, carrying `scenarioValue` — the same identity
 * the risk table is keyed on. Without it a caller cannot tell a stress replay's
 * volatility from the base run's, and every scenario in the table charts
 * whichever row the response happened to list first for each date.
 *
 * Every metric the risk tables can show is selected, not a subset: a column the
 * document omits has no series, and the modal reports that as "no history is
 * published" — indistinguishable, on screen, from a backend that stores none.
 *
 * The per-holding rows nest inside each date, so a holding's own line comes
 * from the same response as the total it contributes to. They are asked for
 * only when a holding's figure was clicked — every leaf of every date is a
 * large payload to carry for a portfolio-level chart that ignores it.
 * They carry no drawdown, no score and no realised ratios; those metrics have
 * no per-position meaning and are blank in the table too.
 *
 * `riskType` narrows the rows server-side to RISK or STRESS, and on this data
 * it is the difference between a usable response and a huge one. One goal
 * portfolio over twenty months:
 *
 *     unfiltered, no holdings      556 rows     543 KB
 *     riskType RISK, no holdings    36 rows      36 KB
 *     unfiltered, with holdings    556 rows    1936 KB
 *     riskType RISK, with holdings  36 rows      96 KB
 *
 * 520 of those 556 rows are stress replays, which a question about the base
 * case never reads. Passed only when the caller is asking about an unstressed
 * run; a stress scenario's own figure still needs the whole set.
 */
export const PORTFOLIO_RISK_HISTORY = gql`
  query PortfolioRiskHistory(
    $securitySymbol: String
    $goalId: Int
    $fromDate: Date
    $toDate: Date
    $riskType: String
    $withHoldings: Boolean! = false
  ) {
    getPortfolioRiskMetricsHistory(securitySymbol: $securitySymbol, goalId: $goalId, fromDate: $fromDate, toDate: $toDate, riskType: $riskType) {
      latestDate
      riskType
      scenarioName
      scenarioValue
      realizedExpectedReturn
      realizedBenchmarkReturn
      realizedVolatility
      realizedVar95
      realizedVar99
      realizedCvar95
      realizedCvar99
      realizedAlpha
      realizedSharpeRatio
      realizedSortinoRatio
      realizedMaxDrawdown
      realizedBetaToBenchmark
      realizedTrackingError
      forecastExpectedReturn
      forecastBenchmarkReturn
      forecastVolatility
      forecastVar95
      forecastVar99
      forecastCvar95
      forecastCvar99
      forecastAlpha
      forecastSharpeRatio
      forecastSortinoRatio
      forecastInformationRatio
      forecastBetaToBenchmark
      forecastCorrelationToBenchmark
      forecastTrackingError
      benchmarkVolatility
      riskVsBenchmark
      riskScore
      holdingRiskContributions @include(if: $withHoldings) {
        holdingSecuritySymbol
        scenarioValue
        weight
        realizedExpectedReturn
        realizedBenchmarkReturn
        realizedVolatility
        realizedVar95
        realizedVar99
        realizedCvar95
        realizedCvar99
        realizedAlpha
        realizedBetaToBenchmark
        realizedTrackingError
        forecastExpectedReturn
        forecastBenchmarkReturn
        forecastVolatility
        forecastVar95
        forecastVar99
        forecastCvar95
        forecastCvar99
        forecastAlpha
        forecastSharpeRatio
        forecastSortinoRatio
        forecastInformationRatio
        forecastBetaToBenchmark
        forecastCorrelationToBenchmark
        forecastTrackingError
      }
    }
  }
`;

/** Per-holding weight and value over time — the series behind a holdings row. */
export const HOLDING_WEIGHTS_HISTORY = gql`
  query HoldingWeightsHistory($securitySymbol: String, $goalId: Int, $fromDate: Date, $toDate: Date) {
    getPortfolioHoldingWeightsHistory(securitySymbol: $securitySymbol, goalId: $goalId, fromDate: $fromDate, toDate: $toDate) {
      latestDate
      holdingSecuritySymbol
      weight
      marketValue
      price
      unrealizedPnl
      totalPnl
    }
  }
`;

/** Per-holding 1-day return contribution over time (a fraction: weight × the day's move). */
export const HOLDING_RETURN_CONTRIBUTIONS_HISTORY = gql`
  query HoldingReturnContributionsHistory($securitySymbol: String, $goalId: Int, $fromDate: Date, $toDate: Date) {
    getPortfolioHoldingReturnContributionsHistory(securitySymbol: $securitySymbol, goalId: $goalId, fromDate: $fromDate, toDate: $toDate) {
      latestDate
      holdingSecuritySymbol
      return1d
    }
  }
`;

/**
 * One dated factor row, shared verbatim by the portfolio and scheme histories —
 * the backend answers both endpoints with the same FactorExposureHistoryPoint.
 *
 * Only what the charts read. The twelve loadings are the ones the exposure bars
 * and the series registry know by name; the nine `*VolAdj` variants the type
 * also carries stay unselected because nothing plots a vol-adjusted loading.
 *
 * The per-holding contribution rows ride behind `$withHoldings`, fetched only
 * when one holding's figure was clicked: they multiply every date by the
 * holding count — 75 rows a date on the fund sampled, 230 KB on top of an
 * 84 KB response before the last four fields were added. The eleven
 * contribution fields mirror the eleven loadings above, and are the two places
 * a holding's figure can be clicked: the exposure drill-down's seven factors
 * and the look-through table's eleven columns. `weight` is the look-through
 * weight cell's series. It moves per disclosure rather than per day — the
 * disclosed weight rides along unchanged between AMC filings — so flat
 * stretches are the expected shape, not a gap. The rows' own asOfDate restates
 * the parent's, `momentumScoreContribution` follows its always-null loading,
 * and the eleven style-tag contributions (rallyHeavy, indexMirror, …) have no
 * reader anywhere.
 */
const FACTOR_HISTORY_POINT = `
      asOfDate
      beta
      alphaScore
      rSquared
      momentumScore
      shortTermMomentumScore
      longTermMomentumScore
      valueScore
      qualityScore
      sizeScore
      growthScore
      highVolatilityScore
      lowVolatilityScore
      holdingFactorContributions @include(if: $withHoldings) {
        holdingSecuritySymbol
        weight
        betaContribution
        alphaScoreContribution
        rSquaredContribution
        shortTermMomentumScoreContribution
        longTermMomentumScoreContribution
        valueScoreContribution
        qualityScoreContribution
        sizeScoreContribution
        growthScoreContribution
        highVolatilityScoreContribution
        lowVolatilityScoreContribution
      }`;

/**
 * Factor exposures over time for a PORTFOLIO.
 *
 * Reshaped by the backend from `{ latestDate, exposures[] }` — whose JSONB
 * column never carried a factor — to flat loadings keyed on `asOfDate`, the
 * same row the scheme history uses. And populated now: 124 dates on the goal
 * portfolio sampled, every loading present, contributions on every date.
 */
export const FACTOR_EXPOSURES_HISTORY = gql`
  query FactorExposuresHistory($securitySymbol: String, $goalId: Int, $fromDate: Date, $toDate: Date, $withHoldings: Boolean! = false) {
    getPortfolioFactorExposuresHistory(securitySymbol: $securitySymbol, goalId: $goalId, fromDate: $fromDate, toDate: $toDate) {
      ${FACTOR_HISTORY_POINT}
    }
  }
`;

/**
 * A scheme's factor loadings over time — the fund-side answer to the portfolio
 * query above.
 *
 * Daily, and deep: 1224 points over five years on the fund sampled, with eleven
 * of the twelve loadings populated on every one. `momentumScore` is always
 * null — the short and long-term momentum scores carry it instead — and
 * `sizeScore` appears on only a fraction of the rows. The contribution rows are
 * younger than the loadings: 9 dates against 241 on the same fund, so a
 * holding's chart starts where that pass began writing.
 *
 * `filter` takes `1y`, `3y` or `5y`. Anything else, `max` included, silently
 * returns the 1y window rather than erroring, so the caller must pick one of
 * the three rather than passing a period through.
 */
export const MF_FACTOR_HISTORY = gql`
  query MfFactorHistory($securitySymbol: String!, $filter: String!, $withHoldings: Boolean! = false) {
    getMutualFundFactorExposuresHistory(securitySymbol: $securitySymbol, filter: $filter) {
      securitySymbol
      filter
      dataPoints
      history {
        ${FACTOR_HISTORY_POINT}
      }
    }
  }
`;

/**
 * Portfolio volatility against its benchmark's, and the tracking error between
 * them, as one pre-joined daily series.
 *
 * The same three figures can be assembled from `PORTFOLIO_RISK_HISTORY`, but
 * that is one row per scenario per date and these come already paired, which is
 * the shape a comparison chart wants.
 *
 * Empty on this backend: 662 points on a goal portfolio and 660 on a scheme,
 * with every one of the three fields null on all of them. Selected so the
 * series appears on its own when the computation starts writing.
 */
export const PORTFOLIO_RISK_TIME_SERIES = gql`
  query PortfolioRiskTimeSeries($securitySymbol: String, $goalId: Int, $startDate: String, $endDate: String) {
    getPortfolio(securitySymbol: $securitySymbol, goalId: $goalId) {
      success
      portfolio {
        securitySymbol
        riskTimeSeries(startDate: $startDate, endDate: $endDate) {
          latestDate
          portfolioVolatility
          benchmarkVolatility
          trackingError
        }
      }
    }
  }
`;

/**
 * A scheme's disclosed positions over time — the fund-side answer to
 * `getPortfolioHoldingWeightsHistory`, which reads `portfolio_holdings_history`
 * and therefore has nothing for a fund.
 *
 * Its own document rather than a field on GET_FUND: it carries every holding of
 * every disclosure and is wanted only when someone opens one holding's chart,
 * so the deep-dive should not drag it along on every fund it loads.
 *
 * Monthly, not daily — the rows come from the per-AMC portfolio disclosures the
 * `loadAmcHoldingsHistoryFromS3` job ingests, so the series has one point per
 * disclosure and starts where that backfill has reached.
 *
 * `weight` here is a PERCENTAGE (the disclosure's weights sum to 100), unlike
 * the portfolio path's fractions.
 */
export const FUND_HOLDINGS_HISTORY = gql`
  query FundHoldingsHistory($securitySymbol: String!) {
    getMetaMutualFund(securitySymbol: $securitySymbol) {
      securitySymbol
      holdingsHistory {
        holdingSymbol
        asOfDate
        weight
        marketValue
        quantity
      }
    }
  }
`;

export const MF_PRICE_HISTORY = gql`
  query MfPriceHistory($securitySymbol: String!, $filter: String!) {
    getMutualFundPriceHistory(securitySymbol: $securitySymbol, filter: $filter) {
      securitySymbol
      dataPoints
      fundHistory { date nav dayEndNav aum }
    }
  }
`;

/**
 * One listed STOCK's daily closes — the price path a fund's trade is drawn on.
 *
 * Keyed on the stock's own NSE symbol (`HDFCBANK.NSE`), which is what the
 * transactions history carries for a listed holding. Debt lines, liquid-fund
 * units and other unlisted positions carry an internal id instead and answer
 * with `dataPoints: 0`, which is a state to render rather than an error.
 *
 * `adjustedClose` is selected beside `close` and preferred: it is the
 * split-adjusted series, so a bonus issue reads as a flat line rather than a
 * crash. Daily and oldest first — 253 rows for a year on the stock sampled.
 * `filter` takes `1y`, `3y`, `5y` and `all`, among others.
 */
export const STOCK_PRICE_HISTORY = gql`
  query StockPriceHistory($securitySymbol: String!, $filter: String!) {
    getStockPriceHistory(securitySymbol: $securitySymbol, filter: $filter) {
      securitySymbol
      dataPoints
      stockHistory { date close adjustedClose }
    }
  }
`;

/**
 * What the manager actually traded, disclosure to disclosure.
 *
 * One point per filing pair — holdings are published monthly, so each point is
 * the difference between two consecutive disclosures. Points arrive OLDEST
 * first.
 *
 * `basis` is selected because it is load-bearing, not for completeness. It
 * names what `action` was judged on, and on the fund sampled a third of rows
 * had quantity and weight moving in OPPOSITE directions: a position sold into a
 * rising price still ends the month a larger share of the fund. A screen that
 * read the weight alone would report those sales as buys.
 *
 * Every percentage arrives as a percentage. `derivedTurnoverPct` is not a
 * second measurement but `periodTurnoverPct` stated at an annual rate, which
 * held exactly on every point sampled — it is taken rather than re-derived so
 * the screen and the backend cannot disagree about the arithmetic.
 *
 * The raw share counts, the ISIN and the holdings totals are deliberately not
 * asked for: nothing on the screen shows them, and the position counts below
 * already say how the book moved.
 *
 * `filter` is the same window vocabulary the price histories take (`1y`, `3y`,
 * `5y`, `all`) and defaults to a year on the backend when left out. The fund
 * sampled has 56 disclosures back to 2021 under `all` against 11 under the
 * default, so a caller marking trades on a multi-year chart must ask for the
 * matching window or the older dots are simply absent.
 */
export const MF_TRANSACTIONS_HISTORY = gql`
  query MfTransactionsHistory($securitySymbol: String!, $filter: String) {
    getMutualFundTransactionsHistory(securitySymbol: $securitySymbol, filter: $filter) {
      history {
        asOfDate
        fromDate
        positionsAdded
        positionsExited
        positionsIncreased
        positionsReduced
        positionsHeld
        periodTurnoverPct
        derivedTurnoverPct
        changes {
          action
          basis
          securitySymbol
          name
          sector
          prevWeight
          currWeight
          weightChange
          quantityChangePct
        }
      }
    }
  }
`;

/**
 * A subject's classification weights over time — the series behind a bucket in
 * the exposure breakdown.
 *
 * Answers with the SAME type as the point-in-time `classifications` field, one
 * element per date, so a caller can hand each element to the helper that
 * already shapes the panel.
 *
 * One axis at a time, and that is not an optimisation but the difference
 * between usable and not. On a goal portfolio over three years:
 *
 *     all seven axes    1331 KB    5.3 s
 *     sectors only       174 KB    0.5 s
 *     industries only   1103 KB    3.4 s
 *
 * Industries is inherently heavy — 78 buckets on every one of 150 dates — so
 * even alone it is a large answer; the others are not.
 *
 * `holdings` is NOT selected: the history tables behind this keep bucket
 * weights only, and the field comes back null on every bucket of every date.
 * The names inside a bucket have their own series in the holding-weight
 * histories, which is where the drill-down rows chart from.
 *
 * `benchmarkWeight` and `relativeWeight` are published for a portfolio and null
 * for a scheme, so the panel's relative reading has history on one side only.
 */
export const CLASSIFICATION_HISTORY = gql`
  query ClassificationHistory(
    $securitySymbol: String!
    $fromDate: Date
    $toDate: Date
    $sectors: Boolean! = false
    $industries: Boolean! = false
    $assetClasses: Boolean! = false
    $securityTypes: Boolean! = false
    $countries: Boolean! = false
    $regions: Boolean! = false
    $currencies: Boolean! = false
  ) {
    getClassificationHistory(securitySymbol: $securitySymbol, fromDate: $fromDate, toDate: $toDate) {
      latestDate
      sectors @include(if: $sectors) { classificationValue weight benchmarkWeight relativeWeight }
      industries @include(if: $industries) { classificationValue weight benchmarkWeight relativeWeight }
      assetClasses @include(if: $assetClasses) { classificationValue weight benchmarkWeight relativeWeight }
      securityTypes @include(if: $securityTypes) { classificationValue weight benchmarkWeight relativeWeight }
      countries @include(if: $countries) { classificationValue weight benchmarkWeight relativeWeight }
      regions @include(if: $regions) { classificationValue weight benchmarkWeight relativeWeight }
      currencies @include(if: $currencies) { classificationValue weight benchmarkWeight relativeWeight }
    }
  }
`;

/**
 * One STOCK's valuation multiples over time — the series behind a cell in the
 * holdings-by-valuation table.
 *
 * `securitySymbol` is the security being valued, not a fund or a portfolio:
 * both answer with an empty list. So this is the one family whose entity is the
 * holding rather than the subject holding it, and a caller passes the stock's
 * own symbol.
 *
 * Daily and deep — 1,895 rows for ICICI Bank back to 2019 — with every ratio
 * populated on every row except `pegRatio`, which appears on about one date in
 * eight. Without a date range it answers the trailing year.
 *
 * `benchmarkWeight` is on the type and deliberately not selected: it is the
 * stock's weight in a fixed market index, not a valuation ratio, and the
 * holdings table's own benchmark column is the fund's benchmark tracker — a
 * different index per fund. Charting one under the other would put two
 * benchmarks in one column.
 */
export const VALUATION_HISTORY = gql`
  query ValuationHistory($securitySymbol: String!, $fromDate: Date, $toDate: Date) {
    getValuationHistory(securitySymbol: $securitySymbol, fromDate: $fromDate, toDate: $toDate) {
      asOfDate
      marketCap
      peRatio
      forwardPe
      pegRatio
      priceBookMrq
      priceSalesTtm
      enterpriseValueEbitda
      dividendYield
      returnOnEquityTtm
    }
  }
`;

/**
 * One fundamental of one stock, through time.
 *
 * `getStockFundamentalsHistory` is the stock master's dated fundamentals
 * record — every figure the stock page's Fundamentals, Risk and Overview tabs
 * print, as it stood on each date. It carries about a hundred fields, and a
 * chart wants one, so the document is built per field rather than selecting
 * the record whole: a three-year series of one figure is a few kilobytes,
 * the same series of all of them is megabytes for a single line.
 *
 * `STOCK_FUNDAMENTAL_FIELDS` is the numeric fields the type carries. Anything
 * else — a trend label, a date — has no series, and the registry refuses it
 * rather than building a document the server would reject.
 */
export const STOCK_FUNDAMENTAL_FIELDS = new Set([
  'return1d', 'return1w', 'return1m', 'return3m', 'return6m', 'returnYtd', 'return1y', 'return3y', 'return5y', 'return10y',
  'marketCap', 'enterpriseValue', 'peRatio', 'forwardPe', 'trailingPe', 'pegRatio', 'priceSalesTtm', 'priceBookMrq',
  'enterpriseValueRevenue', 'enterpriseValueEbitda', 'bookValue', 'ebitda', 'profitMargin', 'operatingMarginTtm',
  'returnOnAssetsTtm', 'returnOnEquityTtm', 'grossMargin', 'netMargin', 'revenueTtm', 'revenuePerShareTtm',
  'grossProfitTtm', 'dilutedEpsTtm', 'earningsShare', 'epsEstimateCurrentYear', 'epsEstimateNextYear',
  'epsGrowthEstimate', 'dividendShare', 'dividendYield', 'payoutRatio', 'sharesOutstanding', 'sharesFloat',
  'percentInsiders', 'percentInstitutions', 'sharesShort', 'shortRatio', 'beta', 'week52High', 'week52Low',
  'day50Ma', 'day200Ma', 'volatilityAnnual', 'volatility30d', 'sharpeRatio', 'maxDrawdown', 'debtToEquity',
  'interestCoverage', 'currentRatio', 'quickRatio', 'assetTurnover', 'fcfMargin', 'priceToFcf', 'rsi14d',
  'macdValue', 'macdSignal', 'macdHistogram', 'peZScore', 'pePercentile', 'revenueGrowth1y', 'epsGrowth1y',
  'revenueCagr3y', 'revenueCagr5y', 'epsCagr3y', 'epsCagr5y', 'earningsBeatRate', 'revenueConsistencyScore',
  'marginConsistencyScore', 'roeConsistencyScore', 'earningsConsistencyScore',
]);

const fundamentalHistoryDocs = new Map();
/** The history document for one field of STOCK_FUNDAMENTAL_FIELDS, built once. */
export const stockFundamentalHistory = (field) => {
  if (!fundamentalHistoryDocs.has(field)) {
    fundamentalHistoryDocs.set(field, gql`
      query StockFundamentalHistory($securitySymbol: String!, $limit: Int!) {
        getStockFundamentalsHistory(securitySymbol: $securitySymbol, limit: $limit) {
          asOfDate
          ${field}
        }
      }
    `);
  }
  return fundamentalHistoryDocs.get(field);
};
